import type { TeamMember } from '../../lib/types'
import type { Mencionable } from '../rich/mentions'

// "Si arrobo a alguien que no está en el proyecto, que me sugiera añadirlo" (Damián).
// Lo PURO de ese flujo, sin React, para probarlo como tabla:
//   · a quién se ofrece en el autocompletar (miembros primero, luego el resto del
//     equipo marcado como de fuera),
//   · quiénes de los mencionados no son miembros (lo que dispara el aviso),
//   · y las @ de un texto plano (el chat), con la MISMA regla que usa el API.
//
// Por qué se ofrece a gente de fuera en vez de esconderla: la mención a un
// no-miembro NO notifica (el API la filtra antes del fan-out para no sacar contenido
// del proyecto a quien no lo puede abrir), así que antes se perdía en silencio.
// Mencionarlo es justo el momento en que uno se da cuenta de que falta.

const minus = (e: string) => e.trim().toLowerCase()

/**
 * Candidatos del autocompletar: los miembros del proyecto (en el orden de siempre,
 * por nombre) y DESPUÉS el resto del equipo activo, marcado `fueraDelProyecto`.
 * `excluir` es quien escribe: mencionarse a uno mismo es ruido.
 */
export function armarMencionables(
  memberEmails: string[],
  team: Pick<TeamMember, 'email' | 'name' | 'photoUrl' | 'isActive'>[],
  excluir?: string,
  /**
   * Si se ofrece a la gente de fuera. Solo a quien es de casa (esDeCasa): el roster
   * trae a TODO el equipo activo, invitados de otros clientes incluidos, y a un
   * invitado —un proveedor, el propio cliente— el @ le enseñaría la lista entera de
   * la agencia y los nombres de la gente de otras cuentas. Además no puede agregar a
   * nadie (PUT /members le da 403), así que la sugerencia no le sirve de nada.
   */
  conFuera = true,
): Mencionable[] {
  const yo = excluir ? minus(excluir) : null
  const miembros = new Set(memberEmails.map(minus))
  const porCorreo = new Map(team.map((t) => [minus(t.email), t]))
  const porNombre = (a: Mencionable, b: Mencionable) => a.name.localeCompare(b.name, 'es')

  const dentro: Mencionable[] = [...miembros]
    .filter((e) => e !== yo)
    .map((e) => {
      const t = porCorreo.get(e)
      return { email: e, name: t?.name || e.split('@')[0] || e, photoUrl: t?.photoUrl ?? null }
    })
    .sort(porNombre)

  // Solo gente ACTIVA del equipo: a un desactivado no se le puede agregar (el PUT
  // lo aceptaría, pero no le llegaría nada y no podría entrar).
  if (!conFuera) return dentro

  const fuera: Mencionable[] = team
    .filter((t) => t.isActive !== false)
    .map((t) => ({ ...t, email: minus(t.email) }))
    .filter((t) => !miembros.has(t.email) && t.email !== yo)
    .map((t) => ({
      email: t.email,
      name: t.name || t.email.split('@')[0] || t.email,
      photoUrl: t.photoUrl ?? null,
      fueraDelProyecto: true,
    }))
    .sort(porNombre)

  return [...dentro, ...fuera]
}

/**
 * De los correos mencionados, los que el autocompletar marcó como de fuera (sin
 * repetir, en el orden en que se mencionaron). Se decide contra la MISMA lista que
 * ofreció el desplegable (`armarMencionables`): así solo cuenta gente del equipo
 * activo —una @ a un correo que nadie conoce no tiene a quién agregar— y el aviso
 * nunca contradice la marca que se vio al elegir.
 */
export function mencionadosFuera(mencionados: string[], mencionables: Mencionable[]): string[] {
  const fuera = new Set(mencionables.filter((m) => m.fueraDelProyecto).map((m) => minus(m.email)))
  const out: string[] = []
  for (const e of mencionados.map(minus)) {
    if (fuera.has(e) && !out.includes(e)) out.push(e)
  }
  return out
}

/**
 * Las @ de un texto plano (composer del chat), con la MISMA regla que
 * `parseMentions` de api/src/routes/chats.ts: `@correo` o `@Nombre completo`, sin
 * distinguir mayúsculas. Si esta regla y la del API se separan, el aviso diría "no
 * le llegó" a alguien a quien sí le llegó (o al revés).
 */
export function mencionesEnTexto(texto: string, candidatos: Pick<Mencionable, 'email' | 'name'>[]): string[] {
  const lower = texto.toLowerCase()
  if (!lower.includes('@')) return []
  const out: string[] = []
  for (const c of candidatos) {
    const tokens = [`@${minus(c.email)}`]
    const nombre = c.name.trim().toLowerCase()
    if (nombre) tokens.push(`@${nombre}`)
    if (tokens.some((t) => lower.includes(t))) out.push(minus(c.email))
  }
  return out
}

/**
 * Los arrobados DE FUERA en un mensaje de chat ya escrito. Parte de la misma regla
 * que `mencionesEnTexto`, pero reparte cada @ a UNA sola persona, empezando por la
 * etiqueta más larga: con Juan (de fuera) y Juan Pérez (del proyecto), "@Juan Pérez"
 * contiene "@juan" y el aviso le decía a quien escribió que Juan no estaba en el
 * proyecto aunque nunca lo arrobó. A la inversa también cuenta: "@Ana Ruiz" es de
 * Ana Ruiz aunque en el proyecto haya una "Ana" a secas.
 */
export function fueraEnTextoDeChat(texto: string, candidatos: Mencionable[]): string[] {
  let resto = texto.toLowerCase()
  if (!resto.includes('@')) return []
  const etiquetas: { token: string; email: string; fuera: boolean }[] = []
  for (const c of candidatos) {
    const email = minus(c.email)
    const fuera = c.fueraDelProyecto === true
    etiquetas.push({ token: `@${email}`, email, fuera })
    const nombre = c.name.trim().toLowerCase()
    if (nombre) etiquetas.push({ token: `@${nombre}`, email, fuera })
  }
  etiquetas.sort((a, b) => b.token.length - a.token.length)
  const out: string[] = []
  for (const { token, email, fuera } of etiquetas) {
    if (!resto.includes(token)) continue
    // Se tapa con un carácter que no es @: la etiqueta ya tiene dueño.
    resto = resto.split(token).join(' ')
    if (fuera && !out.includes(email)) out.push(email)
  }
  return out
}
